import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './CountdownTimer.css';

function CountdownTimer() {
  const [time, setTime] = useState({
    hours: 0,
    minutes: 5,
    seconds: 0
  });
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  
  const intervalRef = useRef(null);
  const audioRef = useRef(null);

  // Presets for quick selection
  const presets = [
    { label: '5 min', minutes: 5 },
    { label: '10 min', minutes: 10 },
    { label: '15 min', minutes: 15 },
    { label: '25 min', minutes: 25 },
    { label: '30 min', minutes: 30 },
    { label: '45 min', minutes: 45 },
    { label: '1 hour', hours: 1 },
    { label: '2 hours', hours: 2 }
  ];

  // Convert time to total seconds
  const timeToSeconds = (timeObj) => {
    return (timeObj.hours * 3600) + (timeObj.minutes * 60) + timeObj.seconds;
  };

  // Convert seconds to time object
  const secondsToTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { hours, minutes, seconds };
  };

  // Start timer
  const startTimer = () => {
    const totalSeconds = timeToSeconds(time);
    if (totalSeconds > 0) {
      setTimeLeft(totalSeconds);
      setIsRunning(true);
      setShowSettings(false);
      setIsFinished(false);
    }
  };

  // Pause/Resume timer
  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  // Reset timer
  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(0);
    setIsFinished(false);
    setShowSettings(true);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // Set preset time
  const setPreset = (preset) => {
    setTime({
      hours: preset.hours || 0,
      minutes: preset.minutes || 0,
      seconds: preset.seconds || 0
    });
  };

  // Timer countdown effect
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            setIsRunning(false);
            setIsFinished(true);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft]);

  // Alarm effect
  useEffect(() => {
    if (isFinished) {
      // Play alarm sound
      if (audioRef.current) {
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
      
      // Show browser notification
      if (Notification.permission === 'granted') {
        new Notification('Timer Finished!', {
          body: 'Your countdown timer has reached zero.',
          icon: '/favicon.ico'
        });
      }
      
      // Flash the page
      document.body.classList.add('timer-finished');
      setTimeout(() => {
        document.body.classList.remove('timer-finished');
      }, 3000);
    }
  }, [isFinished]);

  // Request notification permission on mount
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const currentTime = timeLeft > 0 ? secondsToTime(timeLeft) : { hours: 0, minutes: 0, seconds: 0 };
  const progress = timeLeft > 0 ? (timeLeft / timeToSeconds(time)) * 100 : 0;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Countdown Timer - Online Timer with Alarm",
    "description": "Free online countdown timer with customizable alarms. Perfect for productivity, cooking, workouts, and time management. Set timers and get audio alerts.",
    "url": "https://infi-tools.com/countdown-timer",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Countdown timer with alarm",
      "Customizable time intervals",
      "Audio alerts when timer completes",
      "Pause and resume functionality",
      "Quick preset timers",
      "Visual countdown display",
      "Perfect for productivity and cooking"
    ]
  };

  return (
    <div className="countdown-timer-page">
      <SEO 
        title="Countdown Timer - Online Timer with Alarm & Audio Alert"
        description="Free online countdown timer with customizable alarms. Perfect for productivity, cooking, workouts, and time management. Set custom timers with audio alerts."
        keywords="countdown timer, online timer, timer with alarm, productivity timer, cooking timer, workout timer, time management, countdown clock, timer tool"
        canonicalUrl="https://infi-tools.com/countdown-timer"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="timer-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>Countdown Timer</h1>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="1023063704" style={{ marginBottom: '20px' }} />

        <div className="timer-container">
          {showSettings ? (
            /* Timer Settings */
            <div className="timer-settings">
              <h2>Set Timer</h2>
              
              {/* Quick Presets */}
              <div className="presets">
                <h3>Quick Presets</h3>
                <div className="presets-grid">
                  {presets.map((preset, index) => (
                    <button
                      key={index}
                      className="preset-btn"
                      onClick={() => setPreset(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Time Input */}
              <div className="time-inputs">
                <h3>Custom Time</h3>
                <div className="time-input-group">
                  <div className="time-input">
                    <label>Hours</label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={time.hours}
                      onChange={(e) => setTime({...time, hours: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="time-input">
                    <label>Minutes</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={time.minutes}
                      onChange={(e) => setTime({...time, minutes: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="time-input">
                    <label>Seconds</label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={time.seconds}
                      onChange={(e) => setTime({...time, seconds: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              <button className="start-btn" onClick={startTimer}>
                Start Timer
              </button>
            </div>
          ) : (
            /* Timer Display */
            <div className="timer-display">
              <div className="timer-circle">
                <svg className="progress-ring" width="300" height="300">
                  <circle
                    className="progress-ring-bg"
                    cx="150"
                    cy="150"
                    r="140"
                  />
                  <circle
                    className="progress-ring-progress"
                    cx="150"
                    cy="150"
                    r="140"
                    style={{
                      strokeDashoffset: `${880 - (880 * progress) / 100}`
                    }}
                  />
                </svg>
                <div className="timer-text">
                  <div className="time-display">
                    {String(currentTime.hours).padStart(2, '0')}:
                    {String(currentTime.minutes).padStart(2, '0')}:
                    {String(currentTime.seconds).padStart(2, '0')}
                  </div>
                  {isFinished && (
                    <div className="finished-message">
                      Time's Up! 🔔
                    </div>
                  )}
                </div>
              </div>

              <div className="timer-controls">
                {!isFinished ? (
                  <>
                    <button className="control-btn" onClick={toggleTimer}>
                      {isRunning ? '⏸️ Pause' : '▶️ Resume'}
                    </button>
                    <button className="control-btn secondary" onClick={resetTimer}>
                      🔄 Reset
                    </button>
                  </>
                ) : (
                  <button className="control-btn" onClick={resetTimer}>
                    🔄 New Timer
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="8709982038" style={{ marginTop: '40px' }} />

        {/* Hidden audio element for alarm */}
        <audio ref={audioRef} loop>
          <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEZAztb2uq3e" type="audio/wav" />
        </audio>
      </div>
    </div>
  );
}

export default CountdownTimer; 